import { TrendingUp, TrendingDown } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box,
  Card, 
  CardContent, 
  Typography, 
  Link,
  Skeleton,
  Alert,
  Pagination,
  Stack
} from '@mui/material';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FilterSection from './FilterSection.js';
import axios from 'axios';
import SearchBar from './SearchBar.js';
import { FILTER_TYPES, generateInitialFilterState, handleFilterParams } from './filterConfig.js';

const BGGRankings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltersReady, setIsFiltersReady] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [searchInputValue, setSearchInputValue] = useState('');
  const [filters, setFilters] = useState(generateInitialFilterState());
  const [displayFilters, setDisplayFilters] = useState(generateInitialFilterState());
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isUpdatingFromComponent, setIsUpdatingFromComponent] = useState(false);
  const abortControllerRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState(2025);
  
  const currentYear = new Date().getFullYear();

  // Load all filter data
  useEffect(() => {
    const loadFilterData = async () => {
      setIsLoading(true);
      try {
        const responses = await Promise.all(
          Object.values(FILTER_TYPES).map(async type => {
            const response = await axios.get(type.endpoint);
            return {
              key: type.key,
              data: type.responseTransform(response.data)
            };
          })
        );

        setFilterData(
          responses.reduce((acc, { key, data }) => ({
            ...acc,
            [key]: data
          }), {})
        );
      } catch (error) {
        console.error('Error loading filter data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFilterData();
  }, []);

  // Update filters from URL params
  useEffect(() => {
    // Skip URL decode if the update came from component interaction
    if (isUpdatingFromComponent) {
      setIsUpdatingFromComponent(false);
      return;
    }
    
    setIsFiltersReady(false);
    const newFilters = handleFilterParams.decode(searchParams);
    
    // Synchronize trueSoloOnly checkbox with families filter
    const solitaireOnlyGames = 'Players: Solitaire Only Games';
    if (newFilters.families.includes(solitaireOnlyGames) && !newFilters.trueSoloOnly) {
      newFilters.trueSoloOnly = true;
    }
    
    setFilters(newFilters);
    setDisplayFilters(newFilters);
    setSearchInputValue(newFilters.nameSearchTerm);
    setItemsPerPage(Number(searchParams.get('itemsPerPage') || 25));
    setPage(Number(searchParams.get('page') || 0));
    setIsFiltersReady(true);
  }, [searchParams, isUpdatingFromComponent]);

  // Update URL params from filters
  const updateSearchParams = useCallback((newFilters, newPage, newItemsPerPage) => {
    setIsUpdatingFromComponent(true);
    const params = handleFilterParams.encode(newFilters);
    // Use current URL values as defaults if not provided
    const currentPage = newPage !== undefined ? newPage : Number(searchParams.get('page') || 0);
    const currentItemsPerPage = newItemsPerPage !== undefined ? newItemsPerPage : Number(searchParams.get('itemsPerPage') || 25);
    
    params.set('page', currentPage);
    params.set('itemsPerPage', currentItemsPerPage);
    setSearchParams(params, { replace: true });
  }, [setSearchParams, searchParams]);

  // Fetch rankings when filters or pagination changes
  const fetchRankings = useCallback(async () => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/bgg-survey/groups?page=${page}&size=${itemsPerPage}&year=${selectedYear}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filters),
        signal: abortControllerRef.current.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRankings(data.content || []);
      setTotalPages(data.totalPages || 0);
      // Update display filters to match the successful API call
      setDisplayFilters(filters);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching rankings:', error);
      setError('Failed to load rankings. Please try again later.');
      setRankings([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, filters, selectedYear]);

  useEffect(() => {
    if (isFiltersReady) {
      fetchRankings();
    }
  }, [fetchRankings, isFiltersReady]);

  const handleSearch = useCallback((searchTerm) => {
    const newFilters = { ...displayFilters, nameSearchTerm: searchTerm };
    setFilters(newFilters);
    // Don't update displayFilters here - let it update when API response comes back
    updateSearchParams(newFilters, 0);
  }, [displayFilters, updateSearchParams]);
  
  const handleInputChange = useCallback((newValue) => {
    setSearchInputValue(newValue);
  }, []);
  
  const handleFilterChange = useCallback((type, value) => {
    let newFilters = { ...displayFilters, [type]: value };
    
    // Handle special case for trueSoloOnly checkbox
    if (type === 'trueSoloOnly') {
      const solitaireOnlyGames = 'Players: Solitaire Only Games';
      if (value) {
        // Add solitaire only family filter if not already present
        if (!newFilters.families.includes(solitaireOnlyGames)) {
          newFilters.families = [...newFilters.families, solitaireOnlyGames];
        }
      } else {
        // Remove solitaire only family filter
        newFilters.families = newFilters.families.filter(family => family !== solitaireOnlyGames);
      }
    }
    
    setFilters(newFilters);
    // Don't update displayFilters here - let it update when API response comes back
    updateSearchParams(newFilters, 0); // Reset to page 0 when filters change
  }, [displayFilters, updateSearchParams]);

  const handleWeightChange = useCallback((event, newValue) => {
    const [min, max] = newValue;
    const newFilters = {
      ...displayFilters,
      minAvgWeight: min === 1 ? null : min,
      maxAvgWeight: max === 5 ? null : max
    };
    setFilters(newFilters);
    // Don't update displayFilters here - let it update when API response comes back
    updateSearchParams(newFilters, 0); // Reset to page 0 when filters change
  }, [displayFilters, updateSearchParams]);

  const handleWeightChangePreview = useCallback((minWeight, maxWeight) => {
    // Update display filters for visual feedback only, no URL/API update
    const newDisplayFilters = {
      ...displayFilters,
      minAvgWeight: minWeight,
      maxAvgWeight: maxWeight
    };
    setDisplayFilters(newDisplayFilters);
  }, [displayFilters]);

  const handleFilterSearch = (filterKey) => async (searchTerm) => {
    const filterType = Object.values(FILTER_TYPES).find(type => type.key === filterKey);
    if (!filterType?.searchEndpoint) return [];

    try {
      const response = await axios.get(
        `${filterType.searchEndpoint}?searchTerm=${searchTerm}`
      );
      return filterType.searchTransform(response.data);
    } catch (error) {
      console.error(`Error searching ${filterKey}:`, error);
      return [];
    }
  };

  const handleClearFilters = useCallback(() => {
    const newFilters = generateInitialFilterState();
    setFilters(newFilters);
    // Don't update displayFilters here - let it update when API response comes back
    setSearchInputValue('');
    updateSearchParams(newFilters, 0); // Reset to page 0 when clearing filters
  }, [updateSearchParams]);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage - 1);
    updateSearchParams(filters, newPage - 1); // Explicitly set the new page
  }, [filters, updateSearchParams]);

  const handleItemsPerPageChange = useCallback((event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    updateSearchParams(filters, 0, newItemsPerPage); // Reset to page 0 and set new items per page
  }, [filters, updateSearchParams]);

  const removeFilter = useCallback((type, value) => {
    let newFilters = {
      ...filters,
      [type]: filters[type].filter(item => item !== value)
    };
    
    // If removing "Players: Solitaire Only Games" from families, uncheck trueSoloOnly
    if (type === 'families' && value === 'Players: Solitaire Only Games') {
      newFilters.trueSoloOnly = false;
    }
    
    setFilters(newFilters);
    updateSearchParams(newFilters, 0);
  }, [filters, updateSearchParams]);

  const handleApplySuggestedFilter = useCallback((type, value) => {
    let newFilters = { ...filters };
    
    if (type === 'years') {
      newFilters.years = [...(filters.years || []), value.toString()];
    } else if (type === 'weight') {
      newFilters = {
        ...filters,
        minAvgWeight: 1,
        maxAvgWeight: 3
      };
    }
    
    setFilters(newFilters);
    updateSearchParams(newFilters, 0);
  }, [filters, updateSearchParams]);

  const fetchBGGSuggestions = async (searchTerm) => {
    const response = await axios.post(`/api/bgg-survey/groups?page=0&size=5&year=${selectedYear}`, {
      nameSearchTerm: searchTerm
    });
    return [...new Set(response.data.content
      .flatMap(group => group.games)
      .map(game => game.name))];
  };

  const clearSearch = useCallback(() => {
    setSearchInputValue('');
    const newFilters = { ...filters, nameSearchTerm: '' };
    setFilters(newFilters);
    updateSearchParams(newFilters, 0);
  }, [filters, updateSearchParams]);

  const RankChange = ({ currentRank, lastYearRank }) => {
    if (lastYearRank === undefined || lastYearRank === null) {
      return (
        <Box sx={{ 
          display: 'inline-flex', 
          alignItems: 'center',
          ml: 1,
          bgcolor: 'success.main',
          color: 'white',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontWeight: 'medium'
        }}>
          NEW
        </Box>
      );
    }
    
    const change = lastYearRank - currentRank;
    if (change === 0) return null;
    
    return (
      <Box sx={{ 
        display: 'inline-flex', 
        alignItems: 'center',
        gap: 0.5,
        ml: 1,
        color: 'white',
        backgroundColor: change > 0 ? 'success.main' : 'error.main',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        fontSize: '0.75rem',
        fontWeight: 'medium'
      }}>
        {change > 0 ? (
          <TrendingUp size={14} />
        ) : (
          <TrendingDown size={14} />
        )}
        <span>{change > 0 ? `+${change}` : change}</span>
      </Box>
    );
  };

  const GameCard = ({ game }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2, 
      p: 2,
      '&:hover': {
        bgcolor: 'rgba(0, 0, 0, 0.04)'
      }
    }}>
      <Box
        component="img"
        src={game.thumbnail}
        alt={game.name}
        sx={{
          width: 64,
          height: 64,
          objectFit: 'contain',
          borderRadius: 1,
          boxShadow: 1
        }}
      />
      <Typography 
        variant="h6" 
        sx={{ 
          flex: 1,
          fontSize: '1rem',
          fontWeight: 500
        }}
      >
        {game.name}
      </Typography>
      <Link
        href={`https://boardgamegeek.com/boardgame/${game.id}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5,
          color: 'primary.main',
          textDecoration: 'none',
          px: 2,
          py: 1,
          borderRadius: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'primary.main',
            color: 'white'
          }
        }}
      >
        BGG Page
        <OpenInNewIcon sx={{ fontSize: 16 }} />
      </Link>
    </Box>
  );

  const geeklistLinks = {
    2025: "https://boardgamegeek.com/geeklist/366471/2025-peoples-choice-top-solo-games-1-250",
    2024: "https://boardgamegeek.com/geeklist/345687/2024-peoples-choice-top-200-solo-games-200-21"
  };

  const handleYearChange = useCallback((event) => {
    const newYear = parseInt(event.target.value);
    setSelectedYear(newYear);
    // Reset to page 0 when year changes
    setPage(0);
    updateSearchParams(filters, 0, itemsPerPage);
  }, [filters, itemsPerPage, updateSearchParams]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Box className="bg-white shadow-sm rounded-lg overflow-hidden mb-8 p-6">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <h2 className="text-2xl font-semibold text-slate-800">
            {selectedYear} People's Choice Top Solo Games
          </h2>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" component="label" sx={{ fontWeight: 500 }}>
              Year:
            </Typography>
            <select
              value={selectedYear}
              onChange={handleYearChange}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value={2025}>2025</option>
              <option value={2024}>2024</option>
            </select>
          </Box>
        </Box>
        <Typography variant="body1" component="p" className="text-gray-700 mb-4">
          These rankings show the results of <a href={geeklistLinks[selectedYear]} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">BoardGameGeek's {selectedYear} People's Choice Top Solo Games</a>.<br /><br />
          As part of the survey, similar games were grouped together and those groupings are shown below. If filters are applied, only games in the groups that match those filters will be shown.<br /><br />
          This website is not involved in running the survey and any questions or comments about it should be directed to the link above.<br />
          <RouterLink to="/rankings" className="text-blue-600 hover:underline">Click here</RouterLink> to see Solo Sleuth's calculated solo game rankings instead.
        </Typography>
      </Box>

      <SearchBar
        searchInputValue={searchInputValue}
        onSearch={handleSearch}
        onInputChange={handleInputChange}
        onClearSearch={clearSearch}
        fetchSuggestions={fetchBGGSuggestions}
      />

      {!isLoading && (
        <FilterSection
          filters={displayFilters}
          filterData={filterData}
          itemsPerPage={itemsPerPage}
          onFilterChange={handleFilterChange}
          onWeightChange={handleWeightChange}
          onWeightChangePreview={handleWeightChangePreview}
          onItemsPerPageChange={handleItemsPerPageChange}
          onClearAllFilters={handleClearFilters}
          onApplySuggestedFilter={handleApplySuggestedFilter}
          removeFilter={removeFilter}
          currentYear={currentYear}
          onSearch={handleFilterSearch}
          showMultiplayerFilter={true}
        />
      )}

      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={160} />
          ))
        ) : rankings.length === 0 ? (
          <Alert severity="info">
            No results found. Try adjusting your filters.
          </Alert>
        ) : (
          <>
            {rankings.map((group) => (
              <Card 
                key={`group-${group.groupId}`}
                sx={{ 
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    boxShadow: 2
                  },
                  transition: 'box-shadow 0.2s ease'
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ 
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      minWidth: 0
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        mr: 'auto'
                      }}>
                        <Typography 
                          component="span"
                          sx={{ 
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Rank #{group.rank}
                        </Typography>
                        <RankChange 
                          currentRank={group.rank} 
                          lastYearRank={group.lastYearRank}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      alignItems: 'center',
                      ml: 2,
                      whiteSpace: 'nowrap'
                    }}>
                      <Typography variant="body2">
                        Total Votes: {group.numVotes?.toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        #1 Votes: {group.numOneVotes?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    {group.games.map((game) => (
                      <GameCard key={game.id} game={game} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
            <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
              <Pagination 
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Stack>
          </>
        )}
      </Box>
    </div>
  );
};

export default BGGRankings;